import React, { useState, memo } from 'react';
import { Button } from '../components/ui/Button';
import { Card, CardBody } from '../components/ui/Card';
import { HeartIcon, UsersIcon, VideoIcon, TrophyIcon, GiftIcon, ImageIcon, CalendarIcon, SparklesIcon, GlobeIcon, CheckIcon, ArrowRightIcon, MessageCircleIcon, MapPinIcon, StarIcon, XIcon, Sparkles, Camera, Bell, Share2Icon } from 'lucide-react';
export function DestaPass() {
  const [activeTab, setActiveTab] = useState<'couples' | 'guests'>('couples');
  const calendlyUrl = 'https://calendly.com/ashsingh/destapass-discovery-call';
  const features = [{
    icon: <UsersIcon className="h-8 w-8" data-id="element-809" />,
    title: 'Pre-Wedding Social Network',
    description: 'Connect both families months before the big day. Chat, share stories, and build relationships before you even meet in person.',
    color: 'from-purple-500 to-purple-600'
  }, {
    icon: <GlobeIcon className="h-8 w-8" data-id="element-810" />,
    title: 'Destination & Cultural Guide',
    description: 'Interactive guides about your destination and South Asian wedding traditions. Help every guest feel informed and included.',
    color: 'from-teal-500 to-teal-600'
  }, {
    icon: <Share2Icon className="h-8 w-8" data-id="element-811" />,
    title: 'Live Social Sharing',
    description: 'Your content creator and wedding attendees capture and share amazing live moments. Everyone becomes part of the storytelling experience.',
    color: 'from-pink-500 to-pink-600'
  }, {
    icon: <TrophyIcon className="h-8 w-8" data-id="element-812" />,
    title: 'Quests & Games',
    description: 'Fun challenges, scavenger hunts, and icebreaker activities. Turn strangers into friends through playful engagement.',
    color: 'from-amber-500 to-amber-600'
  }, {
    icon: <GiftIcon className="h-8 w-8" data-id="element-813" />,
    title: 'Exclusive Perks',
    description: 'Curated discounts on local tours, restaurants, and activities. VIP access to pre-wedding events and special experiences.',
    color: 'from-indigo-500 to-indigo-600'
  }, {
    icon: <Camera className="h-8 w-8" data-id="element-814" />,
    title: 'Shared Memories',
    description: 'Centralized photo and video sharing with AI tagging. No more scattered WhatsApp groups—one beautiful collaborative album.',
    color: 'from-rose-500 to-rose-600'
  }, {
    icon: <Bell className="h-8 w-8" data-id="element-815" />,
    title: 'Smart Logistics',
    description: 'Event schedules, transportation coordination, and real-time updates. Keep everyone informed and on time, effortlessly.',
    color: 'from-cyan-500 to-cyan-600'
  }];
  const timeline = [{
    phase: '3 Months Before',
    icon: <SparklesIcon className="h-6 w-6" data-id="element-816" />,
    activities: ['Families join and create profiles', 'Share engagement stories and photos', 'Start cultural education modules', 'Begin icebreaker challenges']
  }, {
    phase: '1 Month Before',
    icon: <MessageCircleIcon className="h-6 w-6" data-id="element-817" />,
    activities: ['Destination guides unlock', 'Group chats heat up', 'Quest activities ramp up', 'Travel coordination begins']
  }, {
    phase: 'Wedding Week',
    icon: <MapPinIcon className="h-6 w-6" data-id="element-818" />,
    activities: ['Live event schedules and updates', 'Real-time transportation coordination', 'Local perks and discounts activate', 'Scavenger hunts during events']
  }, {
    phase: 'During Events',
    icon: <VideoIcon className="h-6 w-6" data-id="element-819" />,
    activities: ['Live social sharing and updates', 'Real-time photo sharing', 'Virtual guest participation', 'Instant memory creation']
  }, {
    phase: 'After Wedding',
    icon: <HeartIcon className="h-6 w-6" data-id="element-820" />,
    activities: ['Collaborative album completion', 'Professional photo galleries', 'Recorded ceremony highlights', 'Continued family connections']
  }];
  const problems = [{
    problem: 'Scattered Communication',
    solution: 'One unified platform for all wedding communication'
  }, {
    problem: "Guests Don't Know Each Other",
    solution: 'Pre-wedding social network builds connections early'
  }, {
    problem: 'Remote Family Missing Out',
    solution: 'Live social sharing brings everyone into the celebration'
  }, {
    problem: 'Cultural Confusion',
    solution: 'Interactive guides educate and include all guests'
  }, {
    problem: 'Lost Photos & Videos',
    solution: 'Centralized, AI-tagged memory collection'
  }, {
    problem: 'Logistics Chaos',
    solution: 'Smart coordination and real-time updates'
  }];
  const stats = [{
    value: '40,000+',
    label: 'Guests Connected'
  }, {
    value: '100,000+',
    label: 'Memories Shared'
  }, {
    value: '98%',
    label: 'Satisfaction Rate'
  }];
  return <div className="bg-gradient-to-b from-purple-50 via-white to-pink-50" data-id="element-821">
      {/* Decorative Pattern Overlay - More Festive */}
      <div className="fixed inset-0 pointer-events-none opacity-5 z-0" data-id="element-822">
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" data-id="element-823">
          <defs data-id="element-824">
            <pattern id="paisley" x="0" y="0" width="150" height="150" patternUnits="userSpaceOnUse" data-id="element-825">
              <path d="M75 10 Q 90 30, 85 50 Q 80 70, 75 75 Q 70 70, 65 50 Q 60 30, 75 10 Z" fill="none" stroke="currentColor" strokeWidth="2" data-id="element-826" />
              <circle cx="75" cy="45" r="8" fill="none" stroke="currentColor" strokeWidth="1" data-id="element-827" />
              <path d="M75 20 L75 30 M70 25 L80 25" stroke="currentColor" strokeWidth="1" data-id="element-828" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#paisley)" className="text-purple-600" data-id="element-829" />
        </svg>
      </div>

      {/* Hero Section - Full Bleed Image */}
      <section className="relative min-h-screen flex items-center overflow-hidden -mt-20" data-id="element-830">
        {/* Background Image - Brighter, Less Dulled */}
        <div className="absolute inset-0" data-id="element-831">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/dBJQSqi7ZjqK7ceqZsgzaN/Hero1.webp" alt="South Asian Wedding Celebration" className="w-full h-full object-cover object-top" data-id="element-832" />
          {/* Lighter Gradient Overlay for Text Readability */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-black/20 to-transparent" data-id="element-833"></div>
        </div>

        {/* Content */}
        <div className="container mx-auto px-4 md:px-6 relative z-10 pt-20" data-id="element-834">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-835">
            {/* Left side - Empty for image visibility */}
            <div className="hidden lg:block" data-id="element-836"></div>

            {/* Right side - Text Content */}
            <div className="text-white" data-id="element-837">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight drop-shadow-lg" data-id="element-838">
                Turn your wedding into a living, breathing digital festival.
              </h1>
              <p className="text-xl md:text-2xl mb-8 leading-relaxed drop-shadow-md" data-id="element-839">
                DestaPass is your private wedding circle—where both sides of the
                family can connect, play, and celebrate together long before
                they meet in person.
              </p>
              <div className="flex flex-col sm:flex-row gap-4" data-id="element-840">
                <a href={calendlyUrl} target="_blank" rel="noopener noreferrer" data-id="element-841">
                  <Button variant="primary" size="lg" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 border-0 shadow-xl font-semibold" data-id="element-842">
                    Book a Demo for Your Wedding
                    <ArrowRightIcon className="ml-2 h-5 w-5" data-id="element-843" />
                  </Button>
                </a>
              </div>
              <div className="mt-8 flex items-center gap-6 text-sm drop-shadow-md" data-id="element-844">
                <div className="flex items-center" data-id="element-845">
                  <CheckIcon className="h-5 w-5 text-green-400 mr-2" data-id="element-846" />
                  <span data-id="element-847">Included in select DestaWed packages</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10 animate-bounce" data-id="element-848">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white drop-shadow-lg" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-849">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" data-id="element-850" />
          </svg>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-12 bg-gradient-to-r from-purple-600 via-pink-600 to-purple-600" data-id="element-851">
        <div className="container mx-auto px-4 md:px-6" data-id="element-852">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-853">
            {stats.map((stat, index) => <div key={index} className="text-center" data-id="element-854">
                <div className="text-4xl md:text-5xl font-bold text-white mb-2" data-id="element-855">
                  {stat.value}
                </div>
                <div className="text-purple-100" data-id="element-856">{stat.label}</div>
              </div>)}
          </div>
        </div>
      </section>

      {/* Problem/Solution - Redesigned */}
      <section className="py-16 md:py-24 bg-white" data-id="element-857">
        <div className="container mx-auto px-4 md:px-6" data-id="element-858">
          <div className="max-w-4xl mx-auto text-center mb-16" data-id="element-859">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" data-id="element-860">
              Traditional Weddings,{' '}
              <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent" data-id="element-861">
                Modern Solutions
              </span>
            </h2>
            <p className="text-xl text-gray-600" data-id="element-862">
              We understand the unique challenges of South Asian weddings.
              DestaPass solves them elegantly.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto" data-id="element-863">
            {problems.map((item, index) => <Card key={index} className="border-2 border-gray-100 hover:border-purple-200 transition-colors overflow-hidden" data-id="element-864">
                <CardBody className="p-0" data-id="element-865">
                  {/* Problem Section - Red Background */}
                  <div className="bg-red-50 p-6 border-b-2 border-gray-100" data-id="element-866">
                    <div className="flex items-center gap-3" data-id="element-867">
                      <div className="flex-shrink-0 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center" data-id="element-868">
                        <XIcon className="h-4 w-4 text-white stroke-[3]" data-id="element-869" />
                      </div>
                      <h3 className="font-semibold text-gray-900 text-lg" data-id="element-870">
                        {item.problem}
                      </h3>
                    </div>
                  </div>
                  {/* Solution Section - Green Background */}
                  <div className="bg-green-50 p-6" data-id="element-871">
                    <div className="flex items-center gap-3" data-id="element-872">
                      <div className="flex-shrink-0 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center" data-id="element-873">
                        <CheckIcon className="h-4 w-4 text-white stroke-[3]" data-id="element-874" />
                      </div>
                      <p className="text-gray-700 font-medium" data-id="element-875">
                        {item.solution}
                      </p>
                    </div>
                  </div>
                </CardBody>
              </Card>)}
          </div>
        </div>
      </section>

      {/* Features Grid - Better Top Padding */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-purple-50 to-pink-50" data-id="element-876">
        <div className="container mx-auto px-4 md:px-6" data-id="element-877">
          <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-878">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" data-id="element-879">
              Everything You Need,
              <br data-id="element-880" />
              <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent" data-id="element-881">
                All in One Place
              </span>
            </h2>
            <p className="text-xl text-gray-600" data-id="element-882">
              Seven powerful features that transform your wedding experience
              from start to finish.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-883">
            {features.map((feature, index) => <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow bg-white" data-id="element-884">
                <CardBody className="p-8 pt-10" data-id="element-885">
                  <div className={`w-16 h-16 bg-gradient-to-br ${feature.color} rounded-2xl flex items-center justify-center text-white mb-8 shadow-lg`} data-id="element-886">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-887">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed" data-id="element-888">
                    {feature.description}
                  </p>
                </CardBody>
              </Card>)}
          </div>
        </div>
      </section>

      {/* Timeline - Better Top Padding */}
      <section className="py-16 md:py-24 bg-white" data-id="element-889">
        <div className="container mx-auto px-4 md:px-6" data-id="element-890">
          <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-891">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" data-id="element-892">
              Your{' '}
              <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent" data-id="element-893">
                Wedding Journey
              </span>
            </h2>
            <p className="text-xl text-gray-600" data-id="element-894">
              From the moment you get engaged to years after, DestaPass is with
              you every step of the way.
            </p>
          </div>
          <div className="max-w-5xl mx-auto" data-id="element-895">
            <div className="relative" data-id="element-896">
              {/* Timeline line */}
              <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-purple-200 via-pink-200 to-purple-200 transform -translate-x-1/2" data-id="element-897"></div>

              <div className="space-y-12" data-id="element-898">
                {timeline.map((phase, index) => <div key={index} className={`relative flex items-center ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`} data-id="element-899">
                    <div className="flex-1 md:pr-12" data-id="element-900">
                      <Card className={`border-2 border-purple-200 shadow-lg ${index % 2 === 0 ? 'md:ml-auto' : 'md:mr-auto'} max-w-md`} data-id="element-901">
                        <CardBody className="p-8 pt-10" data-id="element-902">
                          <div className="flex items-center gap-3 mb-6" data-id="element-903">
                            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white" data-id="element-904">
                              {phase.icon}
                            </div>
                            <h3 className="text-xl font-bold text-gray-900" data-id="element-905">
                              {phase.phase}
                            </h3>
                          </div>
                          <ul className="space-y-2" data-id="element-906">
                            {phase.activities.map((activity, i) => <li key={i} className="flex items-start gap-2 text-gray-600" data-id="element-907">
                                <CheckIcon className="h-5 w-5 text-purple-500 flex-shrink-0 mt-0.5" data-id="element-908" />
                                <span data-id="element-909">{activity}</span>
                              </li>)}
                          </ul>
                        </CardBody>
                      </Card>
                    </div>
                    {/* Timeline node */}
                    <div className="absolute left-1/2 transform -translate-x-1/2 hidden md:flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 text-white font-bold shadow-lg z-10" data-id="element-910">
                      {index + 1}
                    </div>
                    <div className="flex-1 md:pl-12" data-id="element-911"></div>
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* For Couples vs Guests Tabs */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-purple-50 to-pink-50" data-id="element-912">
        <div className="container mx-auto px-4 md:px-6" data-id="element-913">
          <div className="max-w-5xl mx-auto" data-id="element-914">
            {/* Tab Buttons */}
            <div className="flex justify-center mb-12" data-id="element-915">
              <div className="inline-flex bg-white rounded-full p-2 shadow-lg" data-id="element-916">
                <button onClick={() => setActiveTab('couples')} className={`px-8 py-3 rounded-full font-semibold transition-all ${activeTab === 'couples' ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-917">
                  For Couples
                </button>
                <button onClick={() => setActiveTab('guests')} className={`px-8 py-3 rounded-full font-semibold transition-all ${activeTab === 'guests' ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-918">
                  For Guests
                </button>
              </div>
            </div>

            {/* Tab Content */}
            {activeTab === 'couples' ? <div className="grid md:grid-cols-2 gap-8 items-center" data-id="element-919">
                <div data-id="element-920">
                  <h2 className="text-4xl font-bold text-gray-900 mb-6" data-id="element-921">
                    Less Stress,{' '}
                    <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent" data-id="element-922">
                      More Joy
                    </span>
                  </h2>
                  <p className="text-xl text-gray-600 mb-8" data-id="element-923">
                    Focus on what matters—celebrating your love. We handle the
                    digital experience so you don't have to.
                  </p>
                  <div className="space-y-4" data-id="element-924">
                    <div className="flex items-start gap-4" data-id="element-925">
                      <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-926">
                        <CheckIcon className="h-5 w-5 text-purple-600" data-id="element-927" />
                      </div>
                      <div data-id="element-928">
                        <h4 className="font-semibold text-gray-900 mb-1" data-id="element-929">
                          Zero Setup Required
                        </h4>
                        <p className="text-gray-600" data-id="element-930">
                          We configure everything. You just invite your guests.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4" data-id="element-931">
                      <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-932">
                        <CheckIcon className="h-5 w-5 text-purple-600" data-id="element-933" />
                      </div>
                      <div data-id="element-934">
                        <h4 className="font-semibold text-gray-900 mb-1" data-id="element-935">
                          Reduce Planning Chaos
                        </h4>
                        <p className="text-gray-600" data-id="element-936">
                          One platform replaces dozens of WhatsApp groups and
                          email threads.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4" data-id="element-937">
                      <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-938">
                        <CheckIcon className="h-5 w-5 text-purple-600" data-id="element-939" />
                      </div>
                      <div data-id="element-940">
                        <h4 className="font-semibold text-gray-900 mb-1" data-id="element-941">
                          Include Remote Family
                        </h4>
                        <p className="text-gray-600" data-id="element-942">
                          Live social sharing means no one misses your special
                          moments.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4" data-id="element-943">
                      <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-944">
                        <CheckIcon className="h-5 w-5 text-purple-600" data-id="element-945" />
                      </div>
                      <div data-id="element-946">
                        <h4 className="font-semibold text-gray-900 mb-1" data-id="element-947">
                          Preserve Every Memory
                        </h4>
                        <p className="text-gray-600" data-id="element-948">
                          Never lose a photo or video again with centralized
                          collection.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="relative" data-id="element-949">
                  <div className="bg-white rounded-3xl shadow-2xl p-8" data-id="element-950">
                    <div className="text-center mb-6" data-id="element-951">
                      <div className="w-20 h-20 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-952">
                        <Sparkles className="h-10 w-10 text-purple-600" data-id="element-953" />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-954">
                        Included with DestaMeet
                      </h3>
                      <p className="text-gray-600" data-id="element-955">
                        DestaPass comes free with all wedding packages
                      </p>
                    </div>
                    <div className="space-y-3 mb-6" data-id="element-956">
                      <div className="flex items-center justify-between py-3 border-b border-gray-100" data-id="element-957">
                        <span className="text-gray-600" data-id="element-958">Platform Setup</span>
                        <span className="font-semibold text-gray-900" data-id="element-959">
                          Included
                        </span>
                      </div>
                      <div className="flex items-center justify-between py-3 border-b border-gray-100" data-id="element-960">
                        <span className="text-gray-600" data-id="element-961">
                          Live Social Sharing
                        </span>
                        <span className="font-semibold text-gray-900" data-id="element-962">
                          Included
                        </span>
                      </div>
                      <div className="flex items-center justify-between py-3 border-b border-gray-100" data-id="element-963">
                        <span className="text-gray-600" data-id="element-964">Unlimited Guests</span>
                        <span className="font-semibold text-gray-900" data-id="element-965">
                          Included
                        </span>
                      </div>
                      <div className="flex items-center justify-between py-3" data-id="element-966">
                        <span className="text-gray-600" data-id="element-967">Lifetime Access</span>
                        <span className="font-semibold text-gray-900" data-id="element-968">
                          Included
                        </span>
                      </div>
                    </div>
                    <a href={calendlyUrl} target="_blank" rel="noopener noreferrer" className="block" data-id="element-969">
                      <Button variant="primary" className="w-full bg-gradient-to-r from-purple-600 to-pink-600 border-0" data-id="element-970">
                        Request Demo
                      </Button>
                    </a>
                  </div>
                </div>
              </div> : <div className="grid md:grid-cols-2 gap-8 items-center" data-id="element-971">
                <div className="order-2 md:order-1" data-id="element-972">
                  <div className="bg-white rounded-3xl shadow-2xl p-8" data-id="element-973">
                    <div className="text-center mb-6" data-id="element-974">
                      <div className="w-20 h-20 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-975">
                        <UsersIcon className="h-10 w-10 text-purple-600" data-id="element-976" />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-977">
                        Your VIP Experience
                      </h3>
                      <p className="text-gray-600" data-id="element-978">
                        Everything you need for an unforgettable celebration
                      </p>
                    </div>
                    <div className="space-y-4" data-id="element-979">
                      <div className="flex items-start gap-3" data-id="element-980">
                        <HeartIcon className="h-6 w-6 text-pink-500 flex-shrink-0 mt-1" data-id="element-981" />
                        <div data-id="element-982">
                          <h4 className="font-semibold text-gray-900" data-id="element-983">
                            Connect Before You Arrive
                          </h4>
                          <p className="text-sm text-gray-600" data-id="element-984">
                            Meet other guests and build friendships early
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3" data-id="element-985">
                        <GlobeIcon className="h-6 w-6 text-teal-500 flex-shrink-0 mt-1" data-id="element-986" />
                        <div data-id="element-987">
                          <h4 className="font-semibold text-gray-900" data-id="element-988">
                            Learn & Explore
                          </h4>
                          <p className="text-sm text-gray-600" data-id="element-989">
                            Destination guides and cultural education
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3" data-id="element-990">
                        <GiftIcon className="h-6 w-6 text-amber-500 flex-shrink-0 mt-1" data-id="element-991" />
                        <div data-id="element-992">
                          <h4 className="font-semibold text-gray-900" data-id="element-993">
                            Exclusive Perks
                          </h4>
                          <p className="text-sm text-gray-600" data-id="element-994">
                            Discounts on tours, dining, and activities
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3" data-id="element-995">
                        <Camera className="h-6 w-6 text-purple-500 flex-shrink-0 mt-1" data-id="element-996" />
                        <div data-id="element-997">
                          <h4 className="font-semibold text-gray-900" data-id="element-998">
                            Share Memories
                          </h4>
                          <p className="text-sm text-gray-600" data-id="element-999">
                            Upload and access all wedding photos instantly
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="order-1 md:order-2" data-id="element-1000">
                  <h2 className="text-4xl font-bold text-gray-900 mb-6" data-id="element-1001">
                    More Than a Guest,{' '}
                    <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent" data-id="element-1002">
                      Part of the Story
                    </span>
                  </h2>
                  <p className="text-xl text-gray-600 mb-8" data-id="element-1003">
                    DestaPass transforms you from an attendee into an active
                    participant in this beautiful celebration.
                  </p>
                  <div className="space-y-6" data-id="element-1004">
                    <div data-id="element-1005">
                      <h4 className="font-semibold text-gray-900 mb-2" data-id="element-1006">
                        Before the Wedding
                      </h4>
                      <p className="text-gray-600" data-id="element-1007">
                        Connect with other guests, learn about traditions, and
                        get excited together.
                      </p>
                    </div>
                    <div data-id="element-1008">
                      <h4 className="font-semibold text-gray-900 mb-2" data-id="element-1009">
                        During Events
                      </h4>
                      <p className="text-gray-600" data-id="element-1010">
                        Stay informed with schedules, participate in games, and
                        share moments in real-time.
                      </p>
                    </div>
                    <div data-id="element-1011">
                      <h4 className="font-semibold text-gray-900 mb-2" data-id="element-1012">
                        After the Celebration
                      </h4>
                      <p className="text-gray-600" data-id="element-1013">
                        Relive memories, download photos, and stay connected
                        with new friends.
                      </p>
                    </div>
                  </div>
                </div>
              </div>}
          </div>
        </div>
      </section>

      {/* Live Social Sharing Highlight */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-purple-900 via-pink-900 to-purple-900 text-white relative overflow-hidden" data-id="element-1014">
        <div className="absolute inset-0 opacity-10" data-id="element-1015">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" data-id="element-1016">
            <defs data-id="element-1017">
              <pattern id="hearts" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse" data-id="element-1018">
                <text x="50" y="50" fontSize="40" textAnchor="middle" fill="currentColor" data-id="element-1019">
                  💕
                </text>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#hearts)" data-id="element-1020" />
          </svg>
        </div>
        <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-1021">
          <div className="max-w-4xl mx-auto text-center" data-id="element-1022">
            <Share2Icon className="h-16 w-16 mx-auto mb-6 text-pink-300" data-id="element-1023" />
            <h2 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-1024">
              Distance Doesn't Mean Missing Out
            </h2>
            <p className="text-xl text-purple-100 mb-8 leading-relaxed" data-id="element-1025">
              Your content creator and wedding attendees create an amazing live
              experience, capturing and sharing every moment as it happens.
              Remote family and friends aren't just watching—they're part of the
              celebration.
            </p>
            <div className="grid md:grid-cols-3 gap-6 mt-12" data-id="element-1026">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6" data-id="element-1027">
                <div className="text-3xl font-bold mb-2" data-id="element-1028">
                  Multiple Perspectives
                </div>
                <p className="text-purple-100" data-id="element-1029">
                  Your guests capture every moment from different angles
                </p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6" data-id="element-1030">
                <div className="text-3xl font-bold mb-2" data-id="element-1031">Real-Time Sharing</div>
                <p className="text-purple-100" data-id="element-1032">
                  Virtual guests experience moments live as they happen
                </p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6" data-id="element-1033">
                <div className="text-3xl font-bold mb-2" data-id="element-1034">Recorded Forever</div>
                <p className="text-purple-100" data-id="element-1035">
                  Relive every ceremony and moment whenever you want
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cultural Bridge */}
      <section className="py-16 md:py-24 bg-white" data-id="element-1036">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1037">
          <div className="max-w-5xl mx-auto" data-id="element-1038">
            <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-1039">
              <div data-id="element-1040">
                <h2 className="text-4xl font-bold text-gray-900 mb-6" data-id="element-1041">
                  <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent" data-id="element-1042">
                    Everyone Included,
                  </span>{' '}
                  Everyone Informed
                </h2>
                <p className="text-xl text-gray-600 mb-8" data-id="element-1043">
                  South Asian weddings are rich with meaning and tradition.
                  DestaPass helps every guest—regardless of
                  background—understand and appreciate each beautiful moment.
                </p>
                <div className="space-y-6" data-id="element-1044">
                  <div className="flex items-start gap-4" data-id="element-1045">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-100 to-pink-100 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-1046">
                      <span className="text-2xl" data-id="element-1047">📖</span>
                    </div>
                    <div data-id="element-1048">
                      <h4 className="font-semibold text-gray-900 mb-2" data-id="element-1049">
                        Ceremony Explanations
                      </h4>
                      <p className="text-gray-600" data-id="element-1050">
                        Interactive guides explain each ritual, its
                        significance, and what to expect.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4" data-id="element-1051">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-100 to-pink-100 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-1052">
                      <span className="text-2xl" data-id="element-1053">👗</span>
                    </div>
                    <div data-id="element-1054">
                      <h4 className="font-semibold text-gray-900 mb-2" data-id="element-1055">
                        Dress Code Guidance
                      </h4>
                      <p className="text-gray-600" data-id="element-1056">
                        Clear guidance on what to wear for each event, with
                        cultural context.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4" data-id="element-1057">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-100 to-pink-100 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-1058">
                      <span className="text-2xl" data-id="element-1059">🙏</span>
                    </div>
                    <div data-id="element-1060">
                      <h4 className="font-semibold text-gray-900 mb-2" data-id="element-1061">
                        Etiquette Tips
                      </h4>
                      <p className="text-gray-600" data-id="element-1062">
                        Respectful guidance on customs, greetings, and
                        participation.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="relative" data-id="element-1063">
                <div className="bg-gradient-to-br from-purple-100 to-pink-100 rounded-3xl p-8 shadow-xl" data-id="element-1064">
                  <div className="bg-white rounded-2xl p-6 mb-4" data-id="element-1065">
                    <div className="flex items-center gap-3 mb-4" data-id="element-1066">
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white text-xl" data-id="element-1067">
                        💍
                      </div>
                      <h3 className="font-bold text-gray-900" data-id="element-1068">
                        Mehendi Ceremony
                      </h3>
                    </div>
                    <p className="text-sm text-gray-600 mb-3" data-id="element-1069">
                      A joyful pre-wedding celebration where intricate henna
                      designs are applied to the bride's hands and feet,
                      symbolizing beauty, joy, and spiritual awakening.
                    </p>
                    <div className="flex items-center gap-2 text-xs text-purple-600" data-id="element-1070">
                      <span className="bg-purple-100 px-2 py-1 rounded" data-id="element-1071">
                        Duration: 3-4 hours
                      </span>
                      <span className="bg-purple-100 px-2 py-1 rounded" data-id="element-1072">
                        Attire: Colorful
                      </span>
                    </div>
                  </div>
                  <div className="bg-white rounded-2xl p-6" data-id="element-1073">
                    <div className="flex items-center gap-3 mb-4" data-id="element-1074">
                      <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-500 rounded-full flex items-center justify-center text-white text-xl" data-id="element-1075">
                        🔥
                      </div>
                      <h3 className="font-bold text-gray-900" data-id="element-1076">Sangeet Night</h3>
                    </div>
                    <p className="text-sm text-gray-600 mb-3" data-id="element-1077">
                      An evening of music, dance, and celebration where both
                      families perform choreographed dances and celebrate the
                      upcoming union.
                    </p>
                    <div className="flex items-center gap-2 text-xs text-amber-600" data-id="element-1078">
                      <span className="bg-amber-100 px-2 py-1 rounded" data-id="element-1079">
                        Duration: Evening
                      </span>
                      <span className="bg-amber-100 px-2 py-1 rounded" data-id="element-1080">
                        Attire: Festive
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-purple-50 to-pink-50" data-id="element-1081">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1082">
          <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-1083">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" data-id="element-1084">
              Loved by{' '}
              <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent" data-id="element-1085">
                Couples & Guests
              </span>
            </h2>
            <p className="text-xl text-gray-600" data-id="element-1086">
              Real stories from real weddings transformed by DestaPass.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-id="element-1087">
            <Card className="border-0 shadow-xl bg-white" data-id="element-1088">
              <CardBody className="p-8" data-id="element-1089">
                <div className="flex mb-4" data-id="element-1090">
                  {[...Array(5)].map((_, i) => <StarIcon key={i} className="h-5 w-5 text-amber-400 fill-current" data-id="element-1091" />)}
                </div>
                <p className="text-gray-600 mb-6 text-lg leading-relaxed" data-id="element-1092">
                  "DestaPass completely transformed our wedding experience. My
                  grandmother in India was able to watch every ceremony live,
                  and she cried tears of joy. The cultural guides helped our
                  American friends understand and appreciate each tradition. It
                  was magical."
                </p>
                <div className="flex items-center gap-4" data-id="element-1093">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full" data-id="element-1094"></div>
                  <div data-id="element-1095">
                    <div className="font-semibold text-gray-900" data-id="element-1096">
                      Priya & Arjun
                    </div>
                    <div className="text-sm text-gray-600" data-id="element-1097">
                      Married in Cancun, March 2024
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>
            <Card className="border-0 shadow-xl bg-white" data-id="element-1098">
              <CardBody className="p-8" data-id="element-1099">
                <div className="flex mb-4" data-id="element-1100">
                  {[...Array(5)].map((_, i) => <StarIcon key={i} className="h-5 w-5 text-amber-400 fill-current" data-id="element-1101" />)}
                </div>
                <p className="text-gray-600 mb-6 text-lg leading-relaxed" data-id="element-1102">
                  "As a guest, I loved connecting with other attendees before
                  the wedding. By the time we arrived in Mexico, we already had
                  a group of friends. The destination guides and activity
                  discounts made the whole trip so much more enjoyable. Best
                  wedding I've ever attended!"
                </p>
                <div className="flex items-center gap-4" data-id="element-1103">
                  <div className="w-12 h-12 bg-gradient-to-br from-teal-400 to-cyan-400 rounded-full" data-id="element-1104"></div>
                  <div data-id="element-1105">
                    <div className="font-semibold text-gray-900" data-id="element-1106">Sarah M.</div>
                    <div className="text-sm text-gray-600" data-id="element-1107">
                      Wedding Guest, February 2024
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>
        </div>
      </section>

      {/* Final CTA - Redesigned */}
      <section className="relative py-24 md:py-32 overflow-hidden" data-id="element-1108">
        {/* Background with subtle pattern */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-50 via-pink-50 to-purple-50" data-id="element-1109"></div>

        {/* Decorative elements */}
        <div className="absolute top-10 left-10 w-32 h-32 bg-purple-200 rounded-full opacity-20 blur-3xl" data-id="element-1110"></div>
        <div className="absolute bottom-10 right-10 w-40 h-40 bg-pink-200 rounded-full opacity-20 blur-3xl" data-id="element-1111"></div>
        <div className="absolute top-1/2 left-1/4 w-24 h-24 bg-purple-300 rounded-full opacity-10 blur-2xl" data-id="element-1112"></div>

        <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-1113">
          <div className="max-w-4xl mx-auto" data-id="element-1114">
            {/* Main CTA Card */}
            <Card className="border-0 shadow-2xl bg-white overflow-hidden" data-id="element-1115">
              <CardBody className="p-0" data-id="element-1116">
                <div className="grid md:grid-cols-2" data-id="element-1117">
                  {/* Left side - Content */}
                  <div className="p-8 md:p-12 flex flex-col justify-center" data-id="element-1118">
                    <div className="inline-flex items-center px-3 py-1 bg-purple-100 rounded-full text-purple-700 text-sm font-medium mb-4 w-fit" data-id="element-1119">
                      <Sparkles className="h-4 w-4 mr-2" data-id="element-1120" />
                      Limited Availability
                    </div>
                    <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-1121">
                      Ready to Transform Your Wedding?
                    </h2>
                    <p className="text-lg text-gray-600 mb-8" data-id="element-1122">
                      Book a personalized demo and see how DestaPass can turn
                      your celebration into an unforgettable digital festival.
                    </p>
                    <div className="space-y-4" data-id="element-1123">
                      <a href={calendlyUrl} target="_blank" rel="noopener noreferrer" className="block" data-id="element-1124">
                        <Button variant="primary" size="lg" className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 border-0 shadow-lg font-semibold" data-id="element-1125">
                          Book Your Demo Call
                          <ArrowRightIcon className="ml-2 h-5 w-5" data-id="element-1126" />
                        </Button>
                      </a>
                      <p className="text-sm text-gray-500 text-center" data-id="element-1127">
                        Included free with select DestaWed packages
                      </p>
                    </div>
                  </div>

                  {/* Right side - Visual */}
                  <div className="bg-gradient-to-br from-purple-100 to-pink-100 p-8 md:p-12 flex items-center justify-center" data-id="element-1128">
                    <div className="space-y-4 w-full" data-id="element-1129">
                      <div className="bg-white rounded-2xl p-6 shadow-lg transform rotate-2" data-id="element-1130">
                        <div className="flex items-center gap-3 mb-3" data-id="element-1131">
                          <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center" data-id="element-1132">
                            <UsersIcon className="h-5 w-5 text-white" data-id="element-1133" />
                          </div>
                          <div className="flex-1" data-id="element-1134">
                            <div className="h-2 w-24 bg-gray-200 rounded mb-2" data-id="element-1135"></div>
                            <div className="h-2 w-16 bg-gray-100 rounded" data-id="element-1136"></div>
                          </div>
                        </div>
                        <div className="h-2 w-full bg-gray-100 rounded mb-2" data-id="element-1137"></div>
                        <div className="h-2 w-3/4 bg-gray-100 rounded" data-id="element-1138"></div>
                      </div>

                      <div className="bg-white rounded-2xl p-6 shadow-lg transform -rotate-2" data-id="element-1139">
                        <div className="flex items-center gap-3 mb-3" data-id="element-1140">
                          <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-full flex items-center justify-center" data-id="element-1141">
                            <Camera className="h-5 w-5 text-white" data-id="element-1142" />
                          </div>
                          <div className="flex-1" data-id="element-1143">
                            <div className="h-2 w-20 bg-gray-200 rounded mb-2" data-id="element-1144"></div>
                            <div className="h-2 w-12 bg-gray-100 rounded" data-id="element-1145"></div>
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-2" data-id="element-1146">
                          <div className="aspect-square bg-gradient-to-br from-purple-200 to-pink-200 rounded-lg" data-id="element-1147"></div>
                          <div className="aspect-square bg-gradient-to-br from-pink-200 to-purple-200 rounded-lg" data-id="element-1148"></div>
                          <div className="aspect-square bg-gradient-to-br from-purple-200 to-pink-200 rounded-lg" data-id="element-1149"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>
        </div>
      </section>
    </div>;
}